/**
 * PostHogSync - Syncs PostHog analytics state with Clerk auth and consent
 *
 * Initializes PostHog ONLY once the visitor has accepted analytics cookies
 * (see `cookie-consent.ts`) and identifies/resets the user based on Clerk
 * auth state. While consent is undecided or declined, PostHog is never
 * loaded and no identify call fires; revoking consent in-session opts out
 * of capture and resets the identified user. Mirrors the `ClerkAuthSync`
 * pattern.
 *
 * Must be placed inside `ClerkAuthSync` in the provider tree so that
 * auth state is available when this component renders.
 *
 * @since 1.0.0
 */
import { useAtomSet, useAtomValue } from "@effect-atom/atom-react";
import { lazy, Suspense } from "react";
import { clerkAuthStateAtom } from "~/lib/atoms/clerk-auth.ts";
import { cookieConsentAtom } from "~/lib/atoms/cookie-consent.ts";
import { disablePostHog, initPostHog, posthogClientAtom, posthogRef } from "~/lib/atoms/posthog.ts";

/**
 * The user-identify step, code-split behind a dynamic import.
 *
 * Reading `currentUserAtom` pulls `AuthRpcClient` → the Effect RPC layer, which
 * only a signed-in dashboard visitor needs. Loading it lazily — and only when a
 * session exists — keeps that RPC graph out of the always-loaded `main` chunk a
 * marketing visitor downloads (CLA-645).
 *
 * @since 1.0.0
 */
const PostHogIdentify = lazy(() => import("~/components/PostHogIdentify.tsx"));

export interface PostHogSyncProps {
  children: React.ReactNode;
}

export function PostHogSync({ children }: PostHogSyncProps) {
  const consent = useAtomValue(cookieConsentAtom);
  const authState = useAtomValue(clerkAuthStateAtom);
  const setPosthogClient = useAtomSet(posthogClientAtom);

  const consentGranted = consent === "accepted";

  if (typeof window !== "undefined") {
    if (consentGranted) {
      // Initialize PostHog at browser idle (posthog-js is dynamically imported;
      // idempotent, opts back in after an in-session revoke). `onReady` fires
      // exactly once, on the fresh idle init, so the client atom is set without
      // a redundant re-set on later renders.
      initPostHog((client) => {
        setPosthogClient(client);
      });
    } else if (posthogRef.current) {
      // Consent revoked after PostHog was running — stop capture, drop identity
      queueMicrotask(() => {
        disablePostHog();
      });
    }
  }

  // Identify or reset based on auth state — only with analytics consent.
  //
  // The identify path needs the current DB user (`currentUserAtom` → RPC), so it
  // lives in the lazily-loaded {@link PostHogIdentify}; it mounts only when a
  // session exists, so the RPC graph never loads for signed-out marketing
  // visitors. The reset path needs no user data and stays inline.
  const shouldIdentify = consentGranted && posthogRef.current !== null && authState.loaded
    && authState.session !== null;

  if (consentGranted && posthogRef.current && authState.loaded && !authState.session) {
    queueMicrotask(() => {
      posthogRef.current?.reset();
    });
  }

  return (
    <>
      {shouldIdentify && (
        <Suspense fallback={null}>
          <PostHogIdentify />
        </Suspense>
      )}
      {children}
    </>
  );
}
